import { useAccount } from 'frontastic';
import { PublicConfig } from 'frontastic/hooks/useAccount/types';
import { getLocalIdToken, getUserData, getUserMemberType } from 'helpers/padi/cognito';
import { fetchWithIdToken } from 'helpers/utils/fetch';
import { useCallback, useEffect, useState } from 'react';

type GetPublicConfig = () => Promise<PublicConfig>;

const getUsersAvatar = async (idToken: string, affiliateTypeId: string, getPublicConfig: GetPublicConfig) => {
  try {
    const cfg = await getPublicConfig();
    if (!cfg.globalApiDomain) return;
    const response: Response = await fetch(
      `${cfg.globalApiDomain}/p/photos/web/${affiliateTypeId == '3' ? 'logo' : 'certification'}?status=approved`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      },
    );

    const result = await response.json();
    return result?.preSignedURL;
  } catch (e) {
    return null;
  }
};

const getUsersClubEntitlement = async (idToken: string, getPublicConfig: GetPublicConfig) => {
  if (!idToken) return;
  try {
    const cfg = await getPublicConfig();
    if (!cfg.piccoloApiDomain) return;
    const response = await fetchWithIdToken(`${cfg.piccoloApiDomain}/customer/entitlements/active`);
    const result = await response.json();
    if (result) return result;
    return null;
  } catch (e) {
    console.error('----- e: ', e);
    return null;
  }
};

const getUsersEntitlement = async (sub: string, getPublicConfig: GetPublicConfig) => {
  if (!sub) return;
  try {
    const cfg = await getPublicConfig();
    if (!cfg.piccoloApiDomain) return;
    const response: Response = await fetch(`${cfg.piccoloApiDomain}/offer/recommendation?sub=${sub}`, {
      method: 'get',
    });

    const result = await response.json();
    if (result) return result;
    return null;
  } catch (e) {
    console.error('----- e: ', e);
    return null;
  }
};

const getClubSubscription = async (idToken: string, getPublicConfig: GetPublicConfig) => {
  try {
    const cfg = await getPublicConfig();
    if (!cfg.globalApiDomain) return;
    const response: Response = await fetch(`${cfg.globalApiDomain}/c/club/member/subscription`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });

    const result = await response.json();
    if (result) return result;
    return null;
  } catch (e) {
    console.error('----- e: ', e);
    return null;
  }
};

const useGetUserInfo = () => {
  const { getPublicConfig, account } = useAccount();
  const [avatar, setAvatar] = useState<any>();
  const [idToken, setIdToken] = useState<string>();
  const [userData, setUserData] = useState<{ [key: string]: any } | undefined>(undefined);
  const [memberType, setMemberType] = useState<string | undefined>(undefined);
  const [clubEntitlements, setClubEntitlements] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isActicveClubMember, setIsActiveClubMember] = useState<boolean>(false);

  // Cache for entitlements by sub
  const [entitlementsCache, setEntitlementsCache] = useState<Map<string, any[]>>(new Map());

  const getUserClubEntitlments = useCallback(async () => {
    if (clubEntitlements.length > 0) return clubEntitlements; // Return cached data

    let token = idToken;
    if (!token) {
      token = getLocalIdToken();
      setIdToken(token);
    }
    if (!token || !getPublicConfig) return [];

    const response = await getUsersClubEntitlement(token, getPublicConfig);
    if (response) {
      setClubEntitlements(response);
      return response;
    }
    return [];
  }, [getPublicConfig, idToken, clubEntitlements]);

  const getUserEntitlments = useCallback(
    async (sub: string) => {
      // Check cache first
      if (entitlementsCache.has(sub)) {
        return entitlementsCache.get(sub)!;
      }

      if (!getPublicConfig) return [];
      const response = await getUsersEntitlement(sub, getPublicConfig);
      if (response) {
        setEntitlementsCache((prev) => new Map(prev.set(sub, response)));
        return response;
      }
      return [];
    },
    [getPublicConfig, entitlementsCache],
  );

  // Clear all data when account changes (logout)
  useEffect(() => {
    if (!account?.accountId) {
      setAvatar(undefined);
      setIdToken(undefined);
      setUserData(undefined);
      setMemberType(undefined);
      setClubEntitlements([]);
      setEntitlementsCache(new Map());
      setIsLoading(false);
    }
  }, [account?.accountId]);

  useEffect(() => {
    if (isLoading || userData || !account?.accountId) return; // Prevent multiple calls

    setIsLoading(true);
    const idToken = getLocalIdToken();
    if (idToken) setIdToken(idToken);
    const cognitoUserData = getUserData();
    const affiliateId = cognitoUserData?.['custom:affiliate_id'];
    const affiliateTypeId = cognitoUserData?.['custom:affiliate_type_id'];

    const fetchAvatar = async (idToken: string, affiliateTypeId: string) => {
      const userAvatar = await getUsersAvatar(idToken, affiliateTypeId, getPublicConfig);
      setAvatar(userAvatar);
    };

    const getMemberType = getUserMemberType(affiliateTypeId);
    if (getMemberType) setMemberType(getMemberType);

    if (!idToken || !affiliateId) {
      setIsLoading(false);
      return;
    }

    setUserData(cognitoUserData);
    fetchAvatar(idToken, affiliateId).finally(() => setIsLoading(false));
  }, [getPublicConfig, account?.accountId, isLoading, userData]);

  const checkUserActiveClubMember = useCallback(async () => {
    if (isActicveClubMember) return true;
    const idToken = getLocalIdToken();
    if (!idToken) return false;
    const subcriptionInfo = await getClubSubscription(idToken, getPublicConfig);
    if (subcriptionInfo == null) return true;
    const status = subcriptionInfo.subscription?.status?.toLowerCase();
    const isActive = status === 'active' || status === 'trialing';
    setIsActiveClubMember(isActive);
    return isActive;
  }, [getUserEntitlments, setIsActiveClubMember, isActicveClubMember]);

  return {
    avatar,
    idToken,
    userData,
    getUserClubEntitlments,
    getUserEntitlments,
    checkUserActiveClubMember,
    memberType,
    isLoading,
  };
};

export default useGetUserInfo;
